/**
 * AUTOMATION LOGS PAGE
 *
 * View execution history of automation rules.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  DataTable,
  Banner,
  EmptyState,
  Spinner,
  Select,
  Pagination,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface AutomationLog {
  id: number;
  rule_id: number;
  rule_name: string;
  trigger_event: string;
  entity_type: string;
  entity_id: number;
  conditions_evaluated: unknown;
  actions_executed: unknown;
  success: boolean | number;
  error_message: string | null;
  created_at: string;
}

interface LogsResponse {
  logs: AutomationLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export default function AutomationLogs() {
  const navigate = useNavigate();
  const { fetchApi, loading, error } = useApi();
  const [logs, setLogs] = useState<AutomationLog[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let current = true;
    const params = new URLSearchParams({
      page: String(page),
      limit: '50',
    });

    if (statusFilter !== 'all') {
      params.set('success', statusFilter === 'success' ? 'true' : 'false');
    }

    void fetchApi<LogsResponse>(`/automation/logs?${params}`).then(data => {
      if (current && data) {
        setLogs(data.logs || []);
        setTotalPages(data.totalPages || 0);
        setTotalCount(data.total || 0);
      }
    });
    return () => { current = false; };
  }, [fetchApi, page, statusFilter]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr.includes('T') ? dateStr : `${dateStr.replace(' ', 'T')}Z`).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTriggerLabel = (trigger: string) => {
    const labels: Record<string, string> = {
      order_created: 'Order Created',
      order_allocated: 'Order Allocated',
      order_picked: 'Order Picked',
      order_packed: 'Order Packed',
      order_dispatched: 'Order Dispatched',
      stock_low: 'Stock Low',
      return_requested: 'Return Requested',
    };
    return labels[trigger] || trigger;
  };

  const getEntityLink = (log: AutomationLog) => {
    if (log.entity_type === 'order') {
      return `/orders/${log.entity_id}`;
    }
    if (log.entity_type === 'product') {
      return `/products/${log.entity_id}`;
    }
    return null;
  };

  if (loading && logs.length === 0) {
    return (
      <Page title="Automation Logs">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading logs...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Automation Logs"
      subtitle="Execution history of automation rules"
      backAction={{ content: 'Automation Rules', onAction: () => navigate('/automation') }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Filter */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" blockAlign="end">
              <div style={{ width: '200px' }}>
                <Select
                  label="Status"
                  options={[
                    { label: 'All', value: 'all' },
                    { label: 'Success', value: 'success' },
                    { label: 'Failed', value: 'failed' },
                  ]}
                  value={statusFilter}
                  disabled={loading}
                  onChange={(val) => {
                    setStatusFilter(val);
                    setPage(1);
                  }}
                />
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Logs Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {logs.length > 0 ? (
                <>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                    headings={['Time', 'Rule', 'Trigger', 'Entity', 'Actions', 'Status']}
                    rows={logs.map((log) => {
                      const entityLink = getEntityLink(log);
                      return [
                        formatDate(log.created_at),
                        log.rule_name || `Rule ${log.rule_id}`,
                        <Badge key={`trigger-${log.id}`}>{getTriggerLabel(log.trigger_event)}</Badge>,
                        entityLink ? (
                          <a
                            key={`entity-${log.id}`}
                            href={entityLink}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(entityLink);
                            }}
                            style={{ color: '#2c6ecb', textDecoration: 'none' }}
                          >
                            {log.entity_type} #{log.entity_id}
                          </a>
                        ) : (
                          `${log.entity_type} #${log.entity_id}`
                        ),
                        <Text
                          key={`actions-${log.id}`}
                          as="span"
                          tone="subdued"
                          variant="bodySm"
                        >
                          {log.actions_executed === null ? 'No action details retained' : typeof log.actions_executed === 'string' ? log.actions_executed : JSON.stringify(log.actions_executed)}
                        </Text>,
                        log.success ? (
                          <Badge key={`status-${log.id}`} tone="success">Success</Badge>
                        ) : (
                          <BlockStack gap="100" key={`status-${log.id}`}>
                            <Badge tone="critical">Failed</Badge>
                            {log.error_message && (
                              <Text as="span" tone="critical" variant="bodySm">
                                {log.error_message}
                              </Text>
                            )}
                          </BlockStack>
                        ),
                      ];
                    })}
                  />

                  <InlineStack align="center" gap="400">
                    <Pagination
                      hasPrevious={!loading && page > 1}
                      hasNext={!loading && page < totalPages}
                      onPrevious={() => setPage(page - 1)}
                      onNext={() => setPage(page + 1)}
                    />
                    <Text as="span" tone="subdued">
                      Page {page} of {totalPages} ({totalCount} logs)
                    </Text>
                  </InlineStack>
                </>
              ) : (
                <EmptyState
                  heading="No automation logs"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    No saved runs match this filter. Previews do not create logs.
                    Review and activate a supported order rule to record future runs.
                  </p>
                </EmptyState>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
